import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { Box } from "@design-system/Box/Box";
import { Button } from "@design-system/Button/Button";
import { Card } from "@design-system/Card/Card";
import { Spinner } from "@design-system/Spinner/Spinner";
import { Text } from "@design-system/Text/Text";
import { useCompleteMicrosoftOAuthMutation } from "@graphql/integrations/integrations.g";

import { routes } from "@pages/routes";

export const MicrosoftPartnerCenterCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const completeMicrosoftOAuthMutation = useCompleteMicrosoftOAuthMutation({
    onSuccess: (data) => {
      if (data.completeMicrosoftOauth?.errors?.length) {
        const errorMessage =
          data.completeMicrosoftOauth.errors
            .map((e) => e?.message)
            .filter(Boolean)
            .join(", ") || "An error occurred";
        setError(errorMessage);
        return;
      }

      if (data.completeMicrosoftOauth?.connection) {
        setSuccess(true);
      }
    },
    onError: (err: Error) => {
      setError(err.message || "An unexpected error occurred. Please try again.");
    },
  });

  useEffect(() => {
    const code = searchParams.get("code");
    const errorParam = searchParams.get("error");
    const errorDescription = searchParams.get("error_description");

    if (errorParam) {
      setError(
        errorDescription ||
          "An error occurred during authentication. Please try again.",
      );
      return;
    }

    if (!code) {
      setError(
        "Authorization code not found. The authentication process may have been interrupted.",
      );
      return;
    }

    // Call mutation to complete OAuth flow
    completeMicrosoftOAuthMutation.mutate({ code });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleReturnToIntegrations = () => {
    navigate(routes.portal.integrations.$path(), {
      viewTransition: true,
      replace: true,
    });
  };

  // Show success state
  if (success) {
    return (
      <Card>
        <Card.Header withBottomPadding>
          <Card.Header.Title>Connection Successful</Card.Header.Title>
        </Card.Header>
        <Card.Body grow>
          <Box direction="column" gap={6} align="center">
            <Box direction="column" gap={4} align="center">
              <Text color="primary" size={16}>
                Successfully connected to Microsoft Partner Center
              </Text>
              <Text color="textSecondary" size={14}>
                Your Microsoft Partner Center account has been linked successfully.
              </Text>
            </Box>
            <Box justify="center" fullWidth padding="16px 0 0 0">
              <Button color="primary" onClick={handleReturnToIntegrations}>
                Return to Integrations
              </Button>
            </Box>
          </Box>
        </Card.Body>
      </Card>
    );
  }

  // Show error state
  if (error) {
    return (
      <Card>
        <Card.Header withBottomPadding>
          <Card.Header.Title>Connection Failed</Card.Header.Title>
        </Card.Header>
        <Card.Body grow>
          <Box direction="column" gap={6} align="center">
            <Box direction="column" gap={4} align="center">
              <Text color="danger" size={16}>
                Failed to connect to Microsoft Partner Center
              </Text>
              <Text color="textSecondary" size={14}>
                {error}
              </Text>
            </Box>
            <Box justify="center" fullWidth padding="16px 0 0 0">
              <Button color="primary" onClick={handleReturnToIntegrations}>
                Return to Integrations
              </Button>
            </Box>
          </Box>
        </Card.Body>
      </Card>
    );
  }

  // Show loading state

  return (
    <Card>
      <Card.Header withBottomPadding>
        <Card.Header.Title>
          Connecting to Microsoft Partner Center
        </Card.Header.Title>
      </Card.Header>
      <Card.Body grow>
        <Box direction="column" gap={6} align="center">
          <Box direction="column" gap={14}>
            <Box fullWidth align="center" justify="center">
              <Spinner size={32} />
            </Box>
            <Box direction="column" gap={8} align="center">
              <Text size={16}>Your connection is being established...</Text>
              <Text color="textSecondary" size={14}>
                Please wait while we complete the authentication process.
              </Text>
            </Box>
          </Box>
        </Box>
      </Card.Body>
    </Card>
  );
};
